import {
  cn,
  focusHaloFlush,
  HapticSurface,
  panelCard,
  panelMotion,
  panelRow,
  panelRowQuiet,
  press,
} from "@pax/ui";
import { ChevronRight, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";

/** A group of rows on a shared background, with an optional label above and note below. */
export function Group({
  label,
  aside,
  note,
  children,
}: {
  label?: string;
  /** Sits at the other end of the label, such as a count. */
  aside?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      {label ? (
        <div className="flex items-center justify-between gap-3 px-3 pb-1.5">
          <p className="text-xs text-muted-foreground">{label}</p>
          {aside}
        </div>
      ) : null}
      <div className={cn("flex flex-col gap-0.5 p-1", panelCard)}>{children}</div>
      {note ? (
        <p className="px-3 pt-1.5 text-xs text-pretty text-muted-foreground">{note}</p>
      ) : null}
    </div>
  );
}

/** A setting's name, and a line under it, with its control at the end of the row. */
export function SettingRow({
  title,
  description,
  muted = false,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  muted?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(panelRowQuiet, "h-auto min-h-11 gap-4 py-2", muted && "text-muted-foreground")}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{title}</span>
        {description ? (
          <span className="block text-xs text-pretty text-muted-foreground">{description}</span>
        ) : null}
      </span>
      {children}
    </div>
  );
}

/** A setting that is on or off: the whole row is the switch. */
export function SwitchSettingRow({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: {
  title: ReactNode;
  description?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        panelRowQuiet,
        "h-auto min-h-11 w-full gap-4 py-2 text-left hover:bg-foreground/5 disabled:opacity-60",
        focusHaloFlush,
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{title}</span>
        {description ? (
          <span className="block text-xs text-pretty text-muted-foreground">{description}</span>
        ) : null}
      </span>
      <span
        aria-hidden
        className={cn(
          "relative h-5 w-9 shrink-0 rounded-full transition-colors",
          checked ? "bg-foreground" : "bg-foreground/15",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 size-4 rounded-full bg-background shadow-sm transition-[left]",
            checked ? "left-[1.125rem]" : "left-0.5",
          )}
        />
      </span>
    </button>
  );
}

/** A label on the left and a value on the right, for the details of one thing. */
export function DetailLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={cn(panelRowQuiet, "h-auto min-h-10 gap-4 py-2")}>
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 truncate text-right">{children}</span>
    </div>
  );
}

/** A small round button at the end of a row. */
export const quietButton = cn(
  "relative h-7 shrink-0 rounded-full px-3 text-xs font-medium text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground disabled:pointer-events-none disabled:opacity-50",
  panelMotion,
  press,
  focusHaloFlush,
);

/** The round, see-through back button used across the app. */
export const backChrome = cn(
  "relative flex size-9 shrink-0 items-center justify-center rounded-full bg-background/70 text-foreground shadow-sm ring-1 ring-foreground/10",
  panelMotion,
  press,
  focusHaloFlush,
);

/** A row that opens a page outside Pax's app in a new tab, such as a policy. */
export function ExternalLinkRow({ label, href }: { label: string; href: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className={panelRow}>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <ExternalLink aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <HapticSurface />
    </a>
  );
}

/** A row that opens something outside settings from a press, such as Stripe's billing page. */
export function ExternalButtonRow({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      className={cn(panelRow, "disabled:pointer-events-none disabled:opacity-50")}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <ExternalLink aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <HapticSurface />
    </button>
  );
}

/** A pressable row that goes one page deeper: a title, a line under it, the amount, and a chevron. */
export function DrillRow({
  title,
  detail,
  amount,
  amountClassName,
  onOpen,
}: {
  title: string;
  detail: ReactNode;
  amount: string;
  amountClassName?: string;
  onOpen: () => void;
}) {
  return (
    <button type="button" onClick={onOpen} className={cn(panelRow, "h-auto min-h-11 gap-3 py-2")}>
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">{detail}</span>
      </span>
      <span className={cn("shrink-0 tabular-nums", amountClassName)}>{amount}</span>
      <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground/60" />
      <HapticSurface />
    </button>
  );
}

/** A segmented control with one option selected. */
export function Segmented<Value extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly { value: Value; label: string; icon?: typeof ChevronRight }[];
  value: Value;
  onChange: (value: Value) => void;
  label: string;
  className?: string;
}) {
  const chosen = options.findIndex(option => option.value === value);
  return (
    <fieldset
      aria-label={label}
      className={cn("relative grid min-w-0 rounded-full bg-foreground/[0.06] p-0.5", className)}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0.5 left-0.5 rounded-full bg-background shadow-sm ring-1 ring-foreground/10",
          panelMotion,
        )}
        style={{
          width: `calc((100% - 4px) / ${options.length})`,
          transform: `translateX(${chosen * 100}%)`,
        }}
      />
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            "relative flex h-8 items-center justify-center gap-1.5 rounded-full px-3 text-xs font-medium",
            option.value === value
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground",
            panelMotion,
            focusHaloFlush,
          )}
        >
          {option.icon ? <option.icon aria-hidden className="size-3.5" /> : null}
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}
